import { useEffect, useState } from 'react';
import { useNavigate, getRouteApi } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from '@hooks/useTranslation';
import { EorAuthLayout } from './EorAuthLayout';
import { StepTitle, StepSub } from './components/StepBits';
import MailIcon from '@assets/icons/MailIcon';
import CheckIcon from '@assets/icons/CheckIcon';
import { workspaceDiscoveryService } from '@services/workspaceDiscoveryService';
import form from './EorForm.module.scss';
import styles from './CheckInbox.module.scss';

const routeApi = getRouteApi('/eor/check-inbox');

// Terminal resend outcomes — no further resend is possible, so the affordance is
// permanently closed (BE returns resendAvailableIn: 0 for these, which would
// otherwise re-enable the button).
const TERMINAL_RESEND_CODES = new Set([
  'K_WORKSPACE_CHALLENGE_RESEND_CAP_EXCEEDED',
  'K_WORKSPACE_CHALLENGE_EXPIRED',
]);

export function CheckInbox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { email, challengeId, cd } = routeApi.useSearch();
  const [seconds, setSeconds] = useState(cd);
  const [notice, setNotice] = useState('');
  const [resendClosed, setResendClosed] = useState(false);

  // Countdown driver — each render with a positive `seconds` schedules exactly
  // one decrement; cleanup cancels it. No interval/ref, self-evidently correct.
  useEffect(() => {
    if (seconds <= 0) return;
    const id = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [seconds]);

  const resend = useMutation({
    mutationFn: () => workspaceDiscoveryService.resendChallenge(challengeId),
    onSuccess: (res) => {
      setSeconds(res.resendAvailableIn);
      // messageCode reflects RESENT / COOLDOWN / EXPIRED / RESEND_CAP_EXCEEDED.
      setNotice(res.messageCode ? t(res.messageCode, '') : '');
      // Cap-exceeded / expired are terminal — lock the resend so the button
      // (which resendAvailableIn:0 would otherwise re-enable) can't be spammed.
      if (res.messageCode && TERMINAL_RESEND_CODES.has(res.messageCode)) {
        setResendClosed(true);
      }
    },
  });

  return (
    <EorAuthLayout>
      <div className={styles.center}>
        <span className={styles.iconCircle}>
          <MailIcon className={styles.mail} />
        </span>
        <StepTitle>{t('K_DS_EXT_EOR_INBOX_TITLE', 'Check your inbox')}</StepTitle>
        <StepSub>
          {t(
            'K_DS_EXT_EOR_INBOX_SUB',
            "We've sent a link to {email}. Click it to go directly to your workspace.",
          ).replace('{email}', email)}
        </StepSub>

        <div className={styles.notes}>
          <Note
            title={t('K_DS_EXT_EOR_INBOX_EXPIRY_TITLE', 'Link expires in 15 minutes')}
            body={t('K_DS_EXT_EOR_INBOX_EXPIRY_BODY', 'Request a new one if it expires before you open it')}
          />
          <Note
            title={t('K_DS_EXT_EOR_INBOX_DEVICE_TITLE', 'Works on any device')}
            body={t(
              'K_DS_EXT_EOR_INBOX_DEVICE_BODY',
              "Open the email on your phone or desktop — it'll work either way",
            )}
          />
        </div>

        {!resendClosed && (
          <p className={styles.resendRow}>
            {t('K_DS_EXT_EOR_INBOX_DIDNT_GET', "Didn't get it?")}{' '}
            {seconds > 0 ? (
              <span>{t('K_DS_EXT_EOR_INBOX_RESEND_IN', 'Resend in {n}s').replace('{n}', String(seconds))}</span>
            ) : (
              <button
                type="button"
                className={styles.resendBtn}
                disabled={resend.isPending}
                onClick={() => resend.mutate()}
              >
                {t('K_DS_EXT_EOR_INBOX_RESEND', 'Resend link')}
              </button>
            )}
          </p>
        )}
        {notice ? (
          <p className={styles.notice} role="status">
            {notice}
          </p>
        ) : null}

        <p className={form.footerText}>
          {t('K_DS_EXT_EOR_INBOX_TROUBLE', 'Having trouble?')}{' '}
          <button type="button" className={form.link} onClick={() => navigate({ to: '/eor/workspace' })}>
            {t('K_DS_EXT_EOR_INBOX_TRY_URL', 'Try entering a workspace URL')}
          </button>
        </p>
      </div>
    </EorAuthLayout>
  );
}

function Note({ title, body }: { title: string; body: string }) {
  return (
    <div className={styles.note}>
      <span className={styles.noteIcon}>
        <CheckIcon />
      </span>
      <span>
        <span className={styles.noteTitle}>{title}</span>
        <span className={styles.noteBody}>{body}</span>
      </span>
    </div>
  );
}
